var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { isRumReadableText } from './allowlist';
/**
 * Shows an entity's ID in Datadog where its client-defined name would otherwise be masked (CNA-1608):
 * "LobID = 123" instead of "xxxx xxxxx" in Session Replay, and as the click action name.
 *
 * Action names use the SDK's own `data-dd-action-name`. Session Replay has no text-substitution feature,
 * so the swap is done with CSS that only matches inside a replay: the recorder masks every non-stable
 * `data-*` value, so `data-rum-swap` no longer equals RUM_SWAP_LIVE there, while `data-element` (a stable
 * attribute) is recorded as-is and supplies the text. If the SDK ever stops masking `data-*` values the
 * rule simply stops matching and the replay shows the masked name again — nothing leaks.
 */
export var RUM_SWAP_LIVE = 'live';
var REPLAY_ONLY = "[data-rum-swap]:not([data-rum-swap=\"".concat(RUM_SWAP_LIVE, "\"])");
// Same idea for a dropdown: the recorder always masks input values, so the id is overlaid on the field.
var REPLAY_ONLY_INPUT = "[data-rum-swap-input]:not([data-rum-swap-input=\"".concat(RUM_SWAP_LIVE, "\"])");
export var RUM_REPLAY_STYLES = [
    "".concat(REPLAY_ONLY, " > span { display: none !important; }"),
    "".concat(REPLAY_ONLY, "::before { content: attr(data-element); }"),
    "".concat(REPLAY_ONLY_INPUT, " { position: relative; }"),
    "".concat(REPLAY_ONLY_INPUT, " input { color: transparent !important; }"),
    "".concat(REPLAY_ONLY_INPUT, "::before { content: attr(data-element); position: absolute; left: 14px; top: 50%; transform: translateY(-50%); z-index: 1; white-space: nowrap; pointer-events: none; }"),
].join('\n');
var RUM_REPLAY_STYLE_ID = 'rum-replay-styles';
var ENTITY_LABELS = {
    org: 'OrgID',
    client: 'ClientID',
    businessUnit: 'BuID',
    lob: 'LobID',
    subLob: 'SubLobID',
    queue: 'QueueID',
    location: 'LocationID',
    queueLocation: 'QueueLocationID',
    planLocation: 'PlanLocationID',
    plan: 'PlanID',
    scenario: 'ScenarioID',
    multiskillGroup: 'MultiskillGroupID',
    role: 'RoleID',
    shrinkageFactor: 'ShrinkageFactorID',
    integration: 'IntegrationID',
    user: 'UserID',
};
export var rumEntityName = function (kind, id) { return "".concat(ENTITY_LABELS[kind], " = ").concat(id); };
// DB ids are positive; 0 and -1 are used as "none" / "all" sentinels in several pickers.
var isEntityId = function (id) { return typeof id === 'number' && Number.isFinite(id) && id > 0; };
/** Props for the root of a dropdown whose selected value is an entity, so a replay shows its id in the field. */
export var rumEntityInputProps = function (kind, id) {
    return isEntityId(id) ? { 'data-rum-swap-input': RUM_SWAP_LIVE, 'data-element': rumEntityName(kind, id) } : undefined;
};
/**
 * Same, for a dropdown whose selected value is one of our own static options ("Monthly") or a plain value:
 * the recorder masks every input, so the label is overlaid in the replay. Returns nothing for any other text.
 */
export var rumStaticInputProps = function (label) {
    return isRumReadableText(label) ? { 'data-rum-swap-input': RUM_SWAP_LIVE, 'data-element': label } : undefined;
};
/** Adds the replay-only stylesheet once. It has no effect on the live page. */
export var installRumReplayStyles = function () {
    if (document.getElementById(RUM_REPLAY_STYLE_ID))
        return;
    var style = document.createElement('style');
    style.id = RUM_REPLAY_STYLE_ID;
    style.textContent = RUM_REPLAY_STYLES;
    document.head.appendChild(style);
};
/**
 * Wraps a client-defined name so Datadog shows the entity's ID in its place. Renders the name unchanged for
 * users. A name Datadog can already show (a built-in such as "Vacation", or a plain number) is left as it is.
 */
export var RumEntityLabel = function (_a) {
    var kind = _a.kind, id = _a.id, _b = _a.hasActionName, hasActionName = _b === void 0 ? true : _b, children = _a.children;
    if (!isEntityId(id) || isRumReadableText(children))
        return _jsx(_Fragment, { children: children });
    var name = rumEntityName(kind, id);
    return (_jsx("span", __assign({ "data-rum-swap": RUM_SWAP_LIVE, "data-element": name, "data-dd-action-name": hasActionName ? name : undefined }, { children: _jsx("span", { children: children }) })));
};
